import { format, isValid } from "date-fns";
import { MetricCardTrend } from "@/components/common/MetricCard";

/** Shared timestamp label for the dashboard; null when the ISO string is malformed. */
export const formatTimestamp = (iso: string): string | null => {
  const date = new Date(iso);
  return isValid(date) ? format(date, "dd MMM yyyy HH:mm") : null;
};

/**
 * `offerRevenueCents` is in cents; the currency code is not returned by the
 * endpoints — display with the platform default symbol.
 */
export const formatCents = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatCount = (value: number): string =>
  value.toLocaleString("en-US");

/** `module` is a free-form string (e.g. "concierge") — title-case for display. */
export const titleCaseModule = (module: string): string =>
  module
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export interface DeltaBadge {
  delta: string;
  trend?: MetricCardTrend;
}

/**
 * Maps a `delta[key]` value to a MetricCard delta label.
 * `null` means the previous value was 0 — no baseline (never render +∞ or 0%).
 */
export const formatDeltaBadge = (
  value: number | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): DeltaBadge | null => {
  if (value === undefined) {
    return null;
  }
  if (value === null) {
    return {
      delta: t("analytics.platform.delta.noBaseline"),
      trend: "neutral",
    };
  }
  const sign = value > 0 ? "+" : "";
  return {
    delta: t("analytics.platform.delta.vsPrevious", {
      percent: `${sign}${value}`,
    }),
  };
};
